import { useEffect, useState } from "react";
import { CreditCard, Check, ShieldCheck, Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { VoraLogo } from "@/components/brand/VoraLogo";
import { useAuth } from "@/context/AuthContext";
import { api, listResource } from "@/lib/api";
import { mxn } from "@/lib/format";
import { toast } from "sonner";

const CONTACTO_CORPORATIVO = "mailto:hola@felvora.app?subject=Plan%20Corporativo%20FELVORA";

// v4: la unidad de cobro es "crédito" (1 crédito = 1 alta de cliente nuevo con consulta real de listas).
const planDesc = (p) => {
  if (p.es_cotizacion) return "Altas de clientes ilimitadas · usuarios ilimitados";
  const parts = [];
  const c = p.creditos_incluidos;
  if (c === -1) {
    parts.push("altas de clientes ilimitadas");
  } else if (c !== undefined && c !== null) {
    const n = Number(c).toLocaleString("es-MX");
    const extra = p.precio_credito_adicional
      ? ` (+$${Number(p.precio_credito_adicional).toLocaleString("es-MX")}/alta adicional)`
      : "";
    parts.push(`${n} altas de clientes incluidas/mes${extra}`);
  }
  parts.push("usuarios ilimitados");
  return parts.join(" · ");
};

export default function MembresiaPago() {
  const { user, logout } = useAuth();
  const [planes, setPlanes] = useState([]);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  useEffect(() => {
    // ago-2026 · Fase 2 — Catálogo AISLADO POR RÉGIMEN: el backend devuelve solo los planes
    // que esta entidad puede contratar (antes se pedían todos los pagables sin filtrar).
    api.get("/facturacion/planes-disponibles").then((r) => {
      const lista = (r.data?.planes || []).filter((p) => !p.es_cotizacion);
      setPlanes(lista);
      // Preselecciona el plan elegido en el wizard (de la suscripción creada al registrar).
      listResource("suscripciones")
        .then((subs) => {
          const planId = subs?.[0]?.plan_id;
          setSel(planId && lista.some((p) => p.id === planId) ? planId : lista[0]?.id || null);
        })
        .catch(() => setSel(lista[0]?.id || null));
    }).catch(() => {});
  }, []);

  // Retorno desde Stripe Checkout: confirma el pago por polling (mismo patrón que Facturación).
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) return;
    setConfirmando(true);
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/facturacion/checkout/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success("¡Pago confirmado! Tu cuenta pasa a revisión de documentación.");
          window.location.assign("/"); // recarga: el usuario ya está en 'pendiente_aprobacion'
          return;
        }
        if (data.status === "expired") {
          toast.error("La sesión de pago expiró. Intenta de nuevo.");
          window.history.replaceState({}, "", "/");
          setConfirmando(false);
          return;
        }
      } catch (e) { /* reintenta */ }
      if (attempts < 8) setTimeout(poll, 2000);
      else { window.history.replaceState({}, "", "/"); setConfirmando(false); }
    };
    poll();
  }, []);

  const pagar = async () => {
    if (!sel) { toast.error("Elige un paquete para continuar."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/facturacion/checkout", {
        plan_id: sel, origin_url: window.location.origin,
      });
      if (data.url) window.location.href = data.url;
      else { toast.error("No se recibió la URL de pago"); setBusy(false); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar el pago");
      setBusy(false);
    }
  };

  if (confirmando)
    return (
      <div data-testid="membresia-confirmando" className="flex h-full flex-col items-center justify-center gap-3 py-24 text-slate-500">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
        <p className="text-sm">Confirmando tu pago…</p>
      </div>
    );

  return (
    <div data-testid="membresia-pago-screen" className="mx-auto max-w-4xl px-4 py-8 md:py-12">
      <div className="mb-6 flex items-center justify-between">
        <VoraLogo variant="horizontal" tone="dark" />
        <Button data-testid="membresia-logout" variant="ghost" size="sm" onClick={logout}>Cerrar sesión</Button>
      </div>

      <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-5 text-amber-800">
        <Lock className="mt-0.5 h-6 w-6 shrink-0" />
        <div>
          <h1 className="font-heading text-lg font-semibold">Activa tu cuenta con el pago de tu membresía</h1>
          <p className="mt-1 text-sm opacity-90">
            {user?.entity_nombre ? `${user.entity_nombre} · ` : ""}Elige tu paquete y realiza el pago para continuar.
            En cuanto se confirme, pasarás a la carga de documentos para revisión.
          </p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {planes.map((p) => (
          <button
            key={p.id}
            type="button"
            data-testid={`mp-plan-${p.id}`}
            onClick={() => setSel(p.id)}
            className={`rounded-lg border-2 p-5 text-left transition-all ${
              sel === p.id ? "border-accent bg-sage/15 shadow-sm" : "border-slate-200 bg-white hover:border-slate-300"
            }`}
          >
            <p className="font-heading text-base font-semibold text-slate-900">{p.nombre}</p>
            <p className="mt-1 font-heading text-2xl font-bold text-primary tabular-nums">
              {mxn(p.precio_mensual)}<span className="text-xs font-normal text-slate-400">/mes</span>
              <span className="ml-1.5 whitespace-nowrap align-middle text-xs font-normal text-slate-400"
                    data-testid={`precio-mas-iva-${p.id}`}>+ IVA</span>
            </p>
            <p className="mt-2 text-xs text-slate-500">{planDesc(p)}</p>
            <ul className="mt-3 space-y-1 text-xs text-slate-600">
              {(p.modulos || []).slice(0, 5).map((m) => (
                <li key={m} className="flex items-center gap-1.5"><Check className="h-3 w-3 text-emerald-500" /> {m}</li>
              ))}
            </ul>
            {sel === p.id && (
              <p className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-accent">
                <Check className="h-3 w-3" /> Seleccionado
              </p>
            )}
          </button>
        ))}
      </div>

      <p data-testid="mp-corporativo-contacto" className="mt-4 text-sm text-slate-500">
        ¿Necesitas un plan Corporativo / Multi-entidad?{" "}
        <a href={CONTACTO_CORPORATIVO} className="font-medium text-accent hover:underline">Contáctanos</a> para una cotización a la medida.
      </p>

      <div className="mt-8 flex flex-col items-center gap-3">
        <Button data-testid="mp-pagar-btn" onClick={pagar} disabled={busy || !sel} size="lg" className="bg-primary hover:bg-sidebar-hover">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
          Pagar y activar mi cuenta
        </Button>
        <p className="flex items-center gap-1.5 text-xs text-slate-400">
          <ShieldCheck className="h-3.5 w-3.5" /> Pago seguro con Stripe. No almacenamos datos de tu tarjeta.
        </p>
      </div>
    </div>
  );
}
